import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';

import useUsers from '../../context/UsersContext';
import { NotificationType } from '../../config';
import addNotification from '../../utils/addNotification';
import formPrintTemplateService from '../../service/formPrintTemplate.service';
import { PrintableForms } from '../../config/formsWithPrint';
import { DeleteDialogUI, FileUploadDialogUI } from '../Interface';

const normalizeTemplateKey = (key) => {
  if (key === undefined || key === null) {
    return null;
  }

  return String(key);
};

const getTemplateKey = (template) => normalizeTemplateKey(template?.key ?? template?.form?.key);

const isSamePrintableForm = (printableForm, template) => {
  if (printableForm.url !== template?.form?.url) {
    return false;
  }

  return getTemplateKey(template) === normalizeTemplateKey(printableForm.key);
};

const FormPrintTemplate = () => {
  const { getUser, user } = useUsers();
  const [templates, setTemplates] = useState([]);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [removeElement, setRemoveElement] = useState(null);
  const [deleteDialog, setDeletDialog] = useState(false);

  const getTemplates = () => {
    formPrintTemplateService.getTemplates().then((data) => {
      const mapped = PrintableForms.map((pf) => ({
        ...pf,
        template: data.find((d) => isSamePrintableForm(pf, d)),
      }));
      setTemplates(mapped);
    });
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    getTemplates();
  }, []);

  const handleEdit = (form) => {
    setUploadDialog(true);
    setSelectedForm(form);
  };

  const handleCloseUploadDialog = () => {
    setUploadDialog(false);
    setSelectedForm(null);
  };

  const handleDownload = async (template) => {
    await formPrintTemplateService.getFile(template.id, template.name);
  };

  const handleUpload = (files) => {
    if (!selectedForm?.url) {
      return addNotification({
        content: 'Nincs kiválasztott elem!',
        type: NotificationType.ERROR,
      });
    }

    const uploadPromises = files.map((file) =>
      formPrintTemplateService.uploadFile(file, selectedForm.url, user.userId, selectedForm.key)
    );

    Promise.all(uploadPromises)
      .then(() => {
        getTemplates();
      })
      .catch((error) => {
        console.error('Fájl feltöltési hiba:', error);
      });
  };

  const handleDelete = (form) => {
    setRemoveElement(form);
    setDeletDialog(true);
  };

  const hideDeleteDialog = () => {
    setDeletDialog(false);
    setRemoveElement(null);
  };

  const handleRemoveElement = (data) => {
    formPrintTemplateService.deleteFile(removeElement.url, user.userId, removeElement.key).then((data) => {
      hideDeleteDialog();
      getTemplates();
    });
  };

  return (
    <>
      <Box sx={{ bgcolor: 'grey.100', height: '100vh', width: '100vw', position: 'fixed', top: 0 }}>
        <Container maxWidth="md" sx={{ mt: 8 }}>
          <Paper elevation={3} sx={{ py: 5, px: 3, width: '100%', mx: 'auto', borderRadius: 2 }}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Form</TableCell>
                    <TableCell>Riport</TableCell>
                    <TableCell align="center">Műveletek</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templates.map((row) => (
                    <TableRow key={`${row.url}-${row.key ?? 'default'}`}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.template?.name}</TableCell>
                      <TableCell align="center">
                        <IconButton onClick={() => handleEdit(row)} aria-label="edit">
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDelete(row)} aria-label="delete">
                          <DeleteIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDownload(row.template)} aria-label="download">
                          <DownloadIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Container>
      </Box>
      <FileUploadDialogUI
        open={uploadDialog}
        handleClose={handleCloseUploadDialog}
        handleUpload={(files) => {
          handleUpload(files);
          handleCloseUploadDialog();
        }}
      />
      <DeleteDialogUI handleRemoveElement={handleRemoveElement} show={deleteDialog} onHide={() => hideDeleteDialog()} />
    </>
  );
};

export default FormPrintTemplate;
